import {StepperSelectionEvent} from '@angular/cdk/stepper';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatStepper} from '@angular/material/stepper';
import {TranslateService} from '@ngx-translate/core';
import {RefModelStoreService, TakeUntilComponentDestroy} from '@q9elements/ui-kit/common';
import {isEqual} from 'lodash';
import {BehaviorSubject} from 'rxjs';

import {CustomViewDaoService} from '../../../../../../core/services/custom-view-dao.service';
import {ICustomViewFilter} from '../../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../../services/custom-view.service';
import {ViewState, ViewStateType} from '../../models/create-new-view.enum';
import {
  ICustomViewBoby,
  IDialogData,
  ISelectedColumnsEmit,
  ISelectedMetadata
} from '../../models/create-new-view.interface';

@Component({
  selector: 'app-create-new-view-dialog',
  templateUrl: './create-new-view-dialog.component.html',
  styleUrls: ['./create-new-view-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateNewViewDialogComponent
  extends TakeUntilComponentDestroy
  implements OnInit, OnDestroy
{
  public title$: BehaviorSubject<string> = new BehaviorSubject<string>('RUBIC_CUBE.DIALOG_TITLE');
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public view$: BehaviorSubject<ViewStateType> = new BehaviorSubject<ViewStateType>(
    ViewState.SELECT_METADATA
  );

  public viewState = ViewState;
  public disableGlobalViewCreation: boolean;
  public disablePrivateViewCreation: boolean;
  public isViewer: boolean;

  @ViewChild('stepper') private stepper: MatStepper;

  /** Live validity of the Scope step draft. */
  public scopeCompleted = false;
  public filtersReached = false;

  private columnsValid = false;
  /** The scope draft differs from the scope the available columns were fetched for. */
  private scopeDirty = true;
  private lastFetchedScope: ISelectedMetadata | null = null;

  private readonly stepOrder: ViewStateType[] = [
    ViewState.SELECT_METADATA,
    ViewState.SELECT_COLUMNS,
    ViewState.ADD_FILTERS
  ];

  private customGridData: ICustomViewBoby;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDialogData,
    public translate: TranslateService,
    private dialogRef: MatDialogRef<CreateNewViewDialogComponent>,
    private customViewService: CustomViewService,
    private readonly changeRef: ChangeDetectorRef,
    private customViewDaoService: CustomViewDaoService,
    private rmeStore: RefModelStoreService
  ) {
    super();
  }

  ngOnInit(): void {
    // Org model access, not the space role — the server authorises custom view
    // writes from refModel.access (see checkIfUserCanManageViews).
    this.isViewer = !this.rmeStore.isEditor;
    this.disablePrivateViewCreation = !this.data.allowPrivateViewCreate;
    if (this.isViewer || !this.data.allowGlobalViewCreate) {
      this.title$.next('RUBIC_CUBE.DIALOG_TITLE_PRIVATE');
      this.disableGlobalViewCreation = true;
    }

    this.customViewDaoService
      .getCustomViewMetadataList(this.data.refModelId)
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(response => {
        const {list, totalCount} = response;

        this.customViewService.storeRefModelMetadata({
          list,
          totalCount
        });

        this.loading$.next(false);
        this.changeRef.detectChanges();
      });
  }

  public get columnsCompleted(): boolean {
    return this.columnsValid && !this.scopeDirty;
  }

  public get filtersCompleted(): boolean {
    return this.filtersReached && this.columnsCompleted;
  }

  public onScopeStateChange({scope, valid}: {scope: ISelectedMetadata; valid: boolean}): void {
    this.scopeCompleted = valid;
    this.scopeDirty = !this.matchesFetchedScope(scope);
  }

  public onColumnsStateChange({valid}: {valid: boolean}): void {
    this.columnsValid = valid;
  }

  public goToColumnsStep(): void {
    this.enterStep(ViewState.SELECT_COLUMNS);
  }

  public storeSelectedColumns($event: ISelectedColumnsEmit) {
    this.customViewService.storeSelectedColumns($event);
    this.customViewService.pruneStoredFiltersToSelectedColumns();
    this.columnsValid = true;

    this.enterStep(ViewState.ADD_FILTERS);
  }

  public resetFilters() {
    this.enterStep(ViewState.SELECT_COLUMNS);
  }

  public resetColumns() {
    this.enterStep(ViewState.SELECT_METADATA);
  }

  public onStepChange(event: StepperSelectionEvent): void {
    const state = this.stepOrder[event.selectedIndex];
    if (state && state !== this.view$.getValue()) {
      this.enterStep(state);
    }
  }

  /**
   * Single entry point for step navigation (Next buttons and stepper-header clicks
   * both land here): when the scope draft changed since the last fetch, refresh the
   * available columns before rendering a scope-dependent step.
   */
  private enterStep(state: ViewStateType): void {
    if (state === ViewState.SELECT_METADATA || !this.scopeDirty) {
      this.completeEnter(state);

      return;
    }

    const scope = this.customViewService.retrieveSelectedMetadata();

    this.loading$.next(true);
    this.columnsValid = false;

    this.customViewDaoService
      .getAvailableColumnsList(this.data.refModelId, {
        ...(!scope.allNodes && {metadataList: scope.selectedItems}),
        allNodes: scope.allNodes
      })
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(response => {
        this.customViewService.storeRefModelColumns(response);
        this.lastFetchedScope = scope;
        this.scopeDirty = false;
        this.loading$.next(false);
        this.completeEnter(state);
      });
  }

  private completeEnter(state: ViewStateType): void {
    if (state === ViewState.ADD_FILTERS) {
      this.filtersReached = true;
    }

    this.view$.next(state);

    const target = this.stepOrder.indexOf(state);
    if (this.stepper && target !== -1 && this.stepper.selectedIndex !== target) {
      // Linear mode gates the step headers for progressive unlock, but blocks a
      // programmatic jump whose target isn't "completed" yet; lift it for the move.
      const wasLinear = this.stepper.linear;
      this.stepper.linear = false;
      this.stepper.selectedIndex = target;
      this.stepper.linear = wasLinear;
    }

    this.changeRef.detectChanges();
  }

  private matchesFetchedScope(scope: ISelectedMetadata): boolean {
    return (
      !!this.lastFetchedScope &&
      scope.allNodes === this.lastFetchedScope.allNodes &&
      isEqual(scope.selectedItems, this.lastFetchedScope.selectedItems)
    );
  }

  public submit($event: ICustomViewFilter[]): void {
    this.loading$.next(true);

    const filters = this.customViewService.mapFiltersForSio($event);

    const selectedColumnsForm = this.customViewService.retrieveSelectedColumns();
    const selectedMetadataFrom = this.customViewService.retrieveSelectedMetadata();
    const gridName = this.customViewService.retrieveGridName();
    const gridDescription = this.customViewService.retrieveGridDescription();
    const isPrivate = this.customViewService.retrieveIsPrivateGrid();

    this.customGridData = {
      name: gridName,
      description: gridDescription,
      private: isPrivate,
      columns: selectedColumnsForm.selectedColumns.map(column => column.field.value),
      filters,
      metadataList: selectedMetadataFrom.selectedItems,
      allNodes: selectedMetadataFrom.allNodes
    };

    this.createCustomView();
  }

  private createCustomView(): void {
    this.customViewDaoService.createCustomView(this.data.refModelId, this.customGridData).subscribe(
      response => {
        this.customViewService.addNewCustomView(response);

        const msg = this.translate.instant('RUBIC_CUBE.CREATED_SUCCESSFULLY');

        this.dialogRef.close({msg, view: response});
      },
      error => {
        const msg = this.translate.instant(error.error.error || 'RUBIC_CUBE.CREATED_FAILURE');

        this.dialogRef.close({msg});
      }
    );
  }

  ngOnDestroy(): void {
    this.customViewService.resetModal();
  }
}
